import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { XCircle, Archive, RotateCcw, Loader2, History } from "lucide-react";

interface RecordActionsProps {
  id: string;
  onDelete: () => Promise<void>;
  onUnarchive: () => Promise<void>;
  onPreview: () => void;
  isArchived?: boolean;
}

export function RecordActions({ id, onDelete, onUnarchive, onPreview, isArchived }: RecordActionsProps) {
  const navigate = useNavigate();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleClose = useCallback(() => {
    if (!isDeleting) {
      setShowDeleteDialog(false);
    }
  }, [isDeleting]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleClose();
      }
    };

    if (showDeleteDialog) {
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [showDeleteDialog, handleClose]);

  const handleAction = async () => {
    try {
      setIsDeleting(true);
      if (isArchived) {
        await onUnarchive();
      } else {
        await onDelete();
      }
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  return (
    <>
      <div className="flex justify-end gap-2">
        {!isArchived && (
          <>
            <button
              onClick={() => navigate(`/admin/records/${id}/edit`)}
              className="text-brand-primary hover:opacity-70"
            >
              Edit
            </button>
            <button
              onClick={() => navigate(`/admin/records/${id}/versions`)}
              className="inline-flex items-center gap-1 text-brand-primary hover:opacity-70"
              title="View version history"
            >
              <History className="h-3.5 w-3.5" />
              History
            </button>
          </>
        )}
        <button
          onClick={() => setShowDeleteDialog(true)}
          className={isArchived
            ? "text-brand-teal hover:opacity-70"
            : "text-brand-error hover:opacity-70"
          }
        >
          {isArchived ? "Unarchive" : "Archive"}
        </button>
      </div>

      {/* Delete/Unarchive Confirmation Dialog */}
      {showDeleteDialog && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          onClick={handleClose}
        >
          <div
            className="w-[400px] rounded-card bg-brand-bg p-4 shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-3 flex items-center gap-2">
              {isArchived ? (
                <RotateCcw className="h-5 w-5 text-brand-teal" />
              ) : (
                <Archive className="h-5 w-5 text-brand-error" />
              )}
              <h3 className="text-base font-medium">
                {isArchived ? "Unarchive Record" : "Archive Record"}
              </h3>
            </div>
            <p className="mb-4 flex items-center gap-2 text-sm text-brand-muted">
              Are you sure you want to {isArchived ? "unarchive" : "archive"} this record?
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={handleClose}
                className="rounded bg-brand-bg px-3 py-1.5 text-sm font-medium text-brand-muted hover:bg-brand-surface"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                onClick={handleAction}
                disabled={isDeleting}
                className={`inline-flex items-center gap-2 rounded px-3 py-1.5 text-sm font-medium text-brand-surface ${
                  isArchived
                    ? "bg-brand-teal hover:opacity-90"
                    : "bg-brand-error hover:opacity-90"
                }`}
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {isArchived ? "Unarchiving..." : "Archiving..."}
                  </>
                ) : (
                  isArchived ? "Unarchive" : "Archive"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
